// Pure, dependency-free ambiguity prior. Decides whether a query is specific
// enough to investigate directly, or ambiguous enough that we should separate
// identity candidates before spending a full investigation.

import type { DetectedTargetType } from "@/lib/intel/detect";

export type AmbiguityPrior = {
  score: number; // 0 = unique, 100 = extremely ambiguous
  reasons: string[];
  /** Worth spending a candidate-enumeration pass? */
  shouldEnumerate: boolean;
  /**
   * The query is so common that running it as-is would blend several people.
   * The UI must ask for narrowing details before spending an investigation.
   */
  requiresClarification: boolean;
  /** Which extra fields would cut the ambiguity fastest. */
  suggestedClarifiers: string[];
};


export type RefineLike = Partial<Record<
  "dob" | "location" | "address" | "email" | "phone" | "employer" | "aliases" | "notes",
  string
>>;

export function refineSignalCount(refine?: RefineLike): number {
  if (!refine) return 0;
  return Object.values(refine).filter((v) => typeof v === "string" && v.trim().length > 1).length;
}

// Context words inside the query itself that pin a person down.
const INLINE_CONTEXT_RE =
  /\b(ceo|cto|cfo|coo|founder|co-?founder|chairman|chair|director|president|professor|dr\.?|md|mp|minister|mayor|owner|partner|of|at|from|oy|ab|inc|llc|ltd|gmbh)\b/i;

// High-frequency given names and surnames. Not exhaustive by design — it is a
// prior, not a census — but it reliably catches the queries that would
// otherwise blend dozens of different people together.
const COMMON_GIVEN = new Set([
  "james","john","robert","michael","william","david","richard","joseph","thomas","charles","chris","christopher",
  "daniel","matthew","anthony","mark","donald","steven","paul","andrew","joshua","kevin","brian","george","edward",
  "ronald","timothy","jason","jeffrey","ryan","jacob","gary","nicholas","eric","jonathan","stephen","larry","justin",
  "scott","brandon","frank","ben","benjamin","sam","samuel","alex","alexander","peter","jack","harry","tom","mike",
  "mary","patricia","jennifer","linda","elizabeth","barbara","susan","jessica","sarah","karen","nancy","lisa","betty",
  "margaret","sandra","ashley","kimberly","emily","donna","michelle","carol","amanda","dorothy","melissa","deborah",
  "stephanie","rebecca","laura","sharon","cynthia","kathleen","anna","maria","juan","jose","carlos","luis","miguel",
  "antonio","francisco","manuel","pedro","ana","carmen","laura","sofia","wei","li","ming","chen","hui","yan","jun",
  "mohammed","muhammad","mohamed","ahmed","ali","hassan","omar","fatima","aisha","raj","kumar","amit","priya","anil",
  "matti","juha","timo","kari","mikko","jari","antti","pekka","anna","maria","liisa","laura","katri","olli","ville",
]);

const COMMON_SURNAME = new Set([
  "smith","johnson","williams","brown","jones","garcia","miller","davis","rodriguez","martinez","hernandez","lopez",
  "gonzalez","wilson","anderson","thomas","taylor","moore","jackson","martin","lee","perez","thompson","white",
  "harris","sanchez","clark","ramirez","lewis","robinson","walker","young","allen","king","wright","scott","torres",
  "nguyen","hill","flores","green","adams","nelson","baker","hall","rivera","campbell","mitchell","carter","roberts",
  "wang","li","zhang","liu","chen","yang","huang","zhao","wu","zhou","kim","park","choi","singh","patel","kumar",
  "khan","ali","hassan","ahmed","muller","schmidt","schneider","fischer","weber","meyer","wagner","becker",
  "korhonen","virtanen","makinen","nieminen","makela","hamalainen","laine","heikkinen","koskinen","jarvinen",
  "andersson","johansson","karlsson","nilsson","larsson","olsen","hansen","jensen","silva","santos","souza",
]);

function normalizeToken(t: string): string {
  return t.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z]/g, "");
}

export function commonNameAssessment(target: string): { commonParts: string[]; allCommon: boolean } {
  const tokens = target.split(/\s+/).map(normalizeToken).filter((t) => t.length >= 2);
  if (!tokens.length) return { commonParts: [], allCommon: false };
  const commonParts = tokens.filter((t) => COMMON_GIVEN.has(t) || COMMON_SURNAME.has(t));
  return { commonParts, allCommon: commonParts.length === tokens.length };
}

export function estimateAmbiguityPrior(args: {
  target: string;
  targetType: DetectedTargetType | string;
  refine?: RefineLike;
}): AmbiguityPrior {
  const target = (args.target ?? "").trim();
  const reasons: string[] = [];
  const signals = refineSignalCount(args.refine);

  if (["email", "domain", "ip", "wallet", "phone"].includes(args.targetType)) {
    return {
      score: 5,
      reasons: [`A ${args.targetType} is a direct identifier and resolves to a single object.`],
      shouldEnumerate: false,
      requiresClarification: false,
      suggestedClarifiers: [],
    };
  }

  if (args.targetType === "username") {
    const generic = /^[a-z]{1,5}\d{0,3}$/i.test(target) || /^(admin|user|test|info|contact|dev|official)\w{0,6}$/i.test(target);
    const score = generic ? 66 : 30;
    return {
      score,
      reasons: generic
        ? ["Very short or generic handle — commonly reused by unrelated accounts across platforms."]
        : ["Handles are usually unique per platform but can be reused across platforms by different people."],
      shouldEnumerate: generic,
      requiresClarification: generic && signals === 0,
      suggestedClarifiers: generic ? ["Platform where you saw this handle", "Known email", "Real name if known"] : [],
    };
  }

  const tokens = target.split(/\s+/).filter(Boolean);
  const common = commonNameAssessment(target);
  let score: number;

  if (args.targetType === "organization") {
    score = tokens.length <= 1 ? 45 : 22;
    if (tokens.length <= 1) reasons.push("Single-word organisation name — many registrations may share it.");
    if (!/\b(oy|ab|inc|llc|ltd|gmbh|plc|corp|sa|bv|as|nv)\b/i.test(target)) {
      score += 8;
      reasons.push("No legal form in the name — several registered entities may share this trading name.");
    }
  } else {
    // person
    score = 62;
    reasons.push("Personal names are rarely unique on their own.");
    if (tokens.length <= 1) {
      score += 18;
      reasons.push("Only one name token supplied.");
    }
    if (tokens.length >= 3) {
      score -= 12;
      reasons.push("Three or more name parts narrow the field.");
    }
    if (/[A-Z]\.\s/.test(target) || /\b[A-Z]\b/.test(target)) {
      score -= 4;
    }
    if (common.allCommon && common.commonParts.length >= 2) {
      score += 22;
      reasons.push(`“${target}” is built entirely from very common name parts (${common.commonParts.join(", ")}) — likely thousands of matches.`);
    } else if (common.commonParts.length >= 1) {
      score += 10;
      reasons.push(`Contains a very common name part (${common.commonParts.join(", ")}).`);
    }
  }

  if (INLINE_CONTEXT_RE.test(target)) {
    score -= 22;
    reasons.push("The query already carries role or organisation context.");
  }

  if (signals > 0) {
    score -= Math.min(45, signals * 14);
    reasons.push(`${signals} narrowing criteria supplied by the analyst.`);
  }

  score = Math.max(3, Math.min(95, Math.round(score)));

  const suggestedClarifiers =
    args.targetType === "organization"
      ? ["Country / jurisdiction of registration", "Industry or sector", "Registration or VAT number", "Website domain"]
      : ["City & country where they live or work", "Employer or affiliation", "Date or year of birth", "Known email, handle or website"];

  return {
    score,
    reasons,
    shouldEnumerate: score >= 45,
    requiresClarification: score >= 60 && signals === 0,
    suggestedClarifiers,
  };
}

